import React from 'react';
import {motion} from 'framer-motion';
import {config} from '../../config/jewelleryLandingConfig';
import SafeIcon from '../../common/SafeIcon';
import {FiPlay, FiImage, FiCamera} from 'react-icons/fi';

const ActualProof = () => {
  // Ensure exactly 3 sets are rendered
  const proofSets = config.proofSets.slice(0, 3);
  
  // Track failed media to show premium placeholders
  const [failedMedia, setFailedMedia] = React.useState({});
  
  // Map to natural jewellery labels
  const labelMap = {
    "Proof Set 1": "Earring Transformation",
    "Proof Set 2": "Ring Transformation",
    "Proof Set 3": "Necklace Transformation"
  };

  // Handle media load errors
  const handleMediaError = (key) => {
    setFailedMedia(prev => ({...prev, [key]: true}));
  };

  return (
    <section id="proof" className="py-32 relative">
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="text-center mb-24">
          <div className="luxury-section-kicker justify-center mb-6">
            <span>The Transformation</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl luxury-serif-heading mb-6 text-balance">
            See the proof before you enquire
          </h2>
          <p className="text-lg font-light text-muted max-w-3xl mx-auto leading-relaxed">
            Review the exact type of proof BPA Solutions delivers across different categories. Three distinct sets demonstrating the original reference, the finished product image, and the product video preview.
          </p>
        </div>

        <div className="space-y-32">
          {proofSets.map((set, setIndex) => (
            <div key={setIndex} className="relative">
              <div className="flex items-center gap-6 mb-12">
                <span className="text-sm tracking-[0.2em] uppercase text-gold font-bold">
                  {set.category}
                </span>
                <div className="h-[1px] flex-grow bg-gradient-to-r from-gold/40 to-transparent"></div>
                <span className="font-serif text-muted text-lg italic">
                  {labelMap[set.id] || set.id}
                </span>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* 1. Original Product Reference - 9:16 container with object-contain */}
                <motion.div
                  initial={{opacity: 0, y: 20}}
                  whileInView={{opacity: 1, y: 0}}
                  viewport={{once: true}}
                  className="luxury-proof-tile luxury-hover-lift"
                >
                  <div className="flex justify-between items-center mb-6">
                    <div className="text-[10px] uppercase tracking-widest text-muted font-medium">Proof 1</div>
                    <div className="bg-paper border border-champagne px-3 py-1 rounded-full flex items-center gap-2">
                      <SafeIcon icon={FiCamera} className="w-3 h-3 text-gold" />
                      <span className="text-[9px] uppercase tracking-widest text-espresso">Raw Reference</span>
                    </div>
                  </div>
                  <h3 className="luxury-serif-heading text-2xl mb-6">{set.rawImage?.label || "Original Product Reference"}</h3>
                  <div className="aspect-[9/16] overflow-hidden rounded-soft bg-cream relative border border-champagne shadow-inner group">
                    {set.rawImage?.src && !failedMedia[set.rawImage.key] ? (
                      <img
                        src={set.rawImage.src}
                        alt={set.rawImage.alt}
                        onError={() => handleMediaError(set.rawImage.key)}
                        className="w-full h-full object-contain transition-transform duration-1000 group-hover:scale-105 p-4 bg-cream"
                        loading="lazy"
                      />
                    ) : (
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-muted p-6">
                        <span className="text-xs uppercase tracking-widest mb-2">Replace with 1080 x 1920 asset</span>
                        <span className="text-[10px] opacity-50">{set.rawImage?.key}</span>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-muted font-light mt-2">
                    An existing jewellery photo or product page image used as the creative reference.
                  </p>
                </motion.div>

                {/* 2. Finished Product Image - 9:16 with object-cover */}
                <motion.div
                  initial={{opacity: 0, y: 20}}
                  whileInView={{opacity: 1, y: 0}}
                  viewport={{once: true}}
                  transition={{delay: 0.1}}
                  className="luxury-proof-tile ring-2 ring-gold/20 relative z-10 shadow-luxury luxury-hover-lift"
                >
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-gold to-champagne text-espressoDeep text-[10px] uppercase tracking-widest px-6 py-1.5 rounded-full font-bold shadow-goldGlow">
                    Premium Output
                  </div>
                  <div className="flex justify-between items-center mb-6 mt-4">
                    <div className="text-[10px] uppercase tracking-widest text-gold font-bold">Proof 2</div>
                    <div className="bg-paper border border-gold/30 px-3 py-1 rounded-full flex items-center gap-2">
                      <SafeIcon icon={FiImage} className="w-3 h-3 text-gold" />
                      <span className="text-[9px] uppercase tracking-widest text-espresso">Polished Visual</span>
                    </div>
                  </div>
                  <h3 className="luxury-serif-heading text-2xl mb-6">{set.polishedImage?.label || "Finished Product Image"}</h3>
                  <div className="aspect-[9/16] overflow-hidden rounded-soft bg-paper relative border border-gold/20 shadow-inner group">
                    {set.polishedImage?.src && !failedMedia[set.polishedImage.key] ? (
                      <img
                        src={set.polishedImage.src}
                        alt={set.polishedImage.alt}
                        onError={() => handleMediaError(set.polishedImage.key)}
                        className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                        loading="lazy"
                      />
                    ) : (
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-muted">
                        <span className="text-xs uppercase tracking-widest mb-2">Replace with 1080 x 1920 asset</span>
                        <span className="text-[10px] opacity-50">{set.polishedImage?.key}</span>
                      </div>
                    )}
                  </div>
                </motion.div>

                {/* 3. Product Video Preview - 9:16 with object-cover */}
                <motion.div
                  initial={{opacity: 0, y: 20}}
                  whileInView={{opacity: 1, y: 0}}
                  viewport={{once: true}}
                  transition={{delay: 0.2}}
                  className="luxury-proof-tile luxury-hover-lift"
                >
                  <div className="flex justify-between items-center mb-6">
                    <div className="text-[10px] uppercase tracking-widest text-muted font-medium">Proof 3</div>
                    <div className="bg-espressoDeep px-3 py-1 rounded-full flex items-center gap-2 shadow-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
                      <span className="text-[9px] uppercase tracking-widest text-soft">Motion Preview</span>
                    </div>
                  </div>
                  <h3 className="luxury-serif-heading text-2xl mb-6">{set.video?.label || "Product Video Preview"}</h3>
                  <div className="aspect-[9/16] overflow-hidden rounded-soft bg-espressoDeep relative border border-ink shadow-inner group">
                    {set.video?.src && !failedMedia[set.video.key] ? (
                      <video
                        src={set.video.src}
                        poster={set.video.poster}
                        onError={() => handleMediaError(set.video.key)}
                        className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity duration-500"
                        autoPlay
                        muted
                        loop
                        playsInline
                      />
                    ) : (
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-soft">
                        <span className="text-xs uppercase tracking-widest mb-2">Replace with 1080 x 1920 asset</span>
                        <span className="text-[10px] opacity-50">{set.video?.key}</span>
                      </div>
                    )}
                  </div>
                </motion.div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ActualProof;